/**
 * ✅ FASE 11: Hook de Cache Offline
 * Gerencia cache inteligente e sincronização automática
 */

import { useState, useEffect, useCallback } from 'react';
import { cacheService } from '@/services/cacheService';
import { supabase } from '@/integrations/supabase/client';

export type CacheStrategy = 'cache-first' | 'network-first' | 'stale-while-revalidate';

interface UseCacheOptions<T> {
  key: string;
  fetcher: () => Promise<T>;
  strategy?: CacheStrategy;
  ttl?: number; // Time to live em ms
  enabled?: boolean;
}

interface CacheStats {
  cacheSize: number;
  queueSize: number;
  storageUsage?: number;
  storageQuota?: number;
  isOnline: boolean;
}

/**
 * Hook para gerenciar cache com diferentes estratégias
 */
export function useOfflineCache<T>({
  key,
  fetcher,
  strategy = 'stale-while-revalidate',
  ttl,
  enabled = true,
}: UseCacheOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);

  const fetchAndCache = useCallback(async (useCache = true) => {
    try {
      setIsLoading(true);
      setError(null);

      // Tentar cache primeiro se estratégia permitir
      if (useCache && (strategy === 'cache-first' || strategy === 'stale-while-revalidate')) {
        const cached = await cacheService.get<T>(key);
        if (cached) {
          setData(cached);
          setIsStale(strategy === 'stale-while-revalidate');
          setIsLoading(false);
          
          // Se stale-while-revalidate, continuar para buscar dados frescos
          if (strategy === 'cache-first') {
            return cached;
          }
        }
      }

      // Buscar dados da rede
      const freshData = await fetcher();
      
      // Atualizar cache
      await cacheService.set(key, freshData, ttl);
      
      setData(freshData);
      setIsStale(false);
      
      return freshData;
    } catch (err) {
      const error = err as Error;
      setError(error);
      
      // Se falhar e não tiver cache, tentar cache como fallback
      if (strategy === 'network-first') {
        const cached = await cacheService.get<T>(key);
        if (cached) {
          setData(cached);
          setIsStale(true);
          return cached;
        }
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [key, fetcher, strategy, ttl]);

  useEffect(() => {
    if (!enabled) return;

    fetchAndCache();
  }, [enabled, fetchAndCache]);

  const refresh = useCallback(() => {
    return fetchAndCache(false);
  }, [fetchAndCache]);

  const clearCache = useCallback(async () => {
    await cacheService.delete(key);
    setData(null);
  }, [key]);

  return {
    data,
    isLoading,
    error,
    isStale,
    refresh,
    clearCache,
  };
}

/**
 * Hook para monitorar status do cache e conectividade
 */
export function useCacheStats() {
  const [stats, setStats] = useState<CacheStats>({
    cacheSize: 0,
    queueSize: 0,
    isOnline: navigator.onLine,
  });

  const updateStats = useCallback(async () => {
    const cacheStats = await cacheService.getStats();
    setStats({
      ...cacheStats,
      isOnline: cacheService.getOnlineStatus(),
    });
  }, []);

  useEffect(() => {
    updateStats();

    // Atualizar quando status online mudar
    const handleOnline = () => {
      setStats(prev => ({ ...prev, isOnline: true }));
      updateStats();
    };

    const handleOffline = () => {
      setStats(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Atualizar periodicamente
    const interval = setInterval(updateStats, 30000); // 30s

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [updateStats]);

  const clearAllCache = useCallback(async () => {
    await cacheService.clear();
    await updateStats();
  }, [updateStats]);

  const clearExpiredCache = useCallback(async () => {
    await cacheService.clearExpired();
    await updateStats();
  }, [updateStats]);

  return {
    ...stats,
    refresh: updateStats,
    clearAll: clearAllCache,
    clearExpired: clearExpiredCache,
  };
}

/**
 * Hook para operações offline
 */
export function useOfflineOperations() {
  const [isPending, setIsPending] = useState(false);

  const queueOperation = useCallback(async (
    operation: 'create' | 'update' | 'delete',
    table: string,
    data: any
  ) => {
    setIsPending(true);
    try {
      await cacheService.queueOperation(operation, table, data);
      return true;
    } catch (error) {
      console.error('Erro ao enfileirar operação:', error);
      return false;
    } finally {
      setIsPending(false);
    }
  }, []);

  return {
    queueOperation,
    isPending,
  };
}
